import { Component, inject, input, output, resource, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  BbitFileUtils,
  BbitPlaceBookingFileUploadType,
  IBbitPlaceBookingFileLinkRequest,
  IBbitPlaceBookingMetaResponse,
  IBbitPrepareFileUploadRequest,
  IBbitPrepareFileUploadResponse,
} from '@bbitgmbh/bbit.utils';
import { BbitWebsiteService, I18nPipe } from '@bbitgmbh/website.sdk';
import { LoadingComponent } from '../../helper/loading/loading.component';
import { PlaceBookingService } from '../booking.service';

@Component({
  selector: 'app-know-how-upload',
  imports: [FormsModule, LoadingComponent, I18nPipe],
  templateUrl: './know-how-upload.component.html',
  styleUrl: './know-how-upload.component.scss',
})
export class KnowHowUploadComponent {
  private _service = inject(PlaceBookingService);
  private _website = inject(BbitWebsiteService);
  public fileType = input.required<BbitPlaceBookingFileUploadType>();
  public reload = output<void>();
  public meta = resource<IBbitPlaceBookingMetaResponse, void>({
    loader: async () => {
      const meta = await this._service.getMeta();
      return meta;
    },
  });
  public isUploading = signal(false);

  dragLeave(event: Event) {
    const element = event.target as HTMLInputElement;
    element.classList.add('border-dashed');
    element.classList.remove('border-solid');
    element.classList.remove('bg-primary-600');
    event.preventDefault();
    event.stopPropagation();
  }

  async uploadFile(event: DragEvent | Event): Promise<void> {
    try {
      this.isUploading.set(true);
      event.preventDefault();
      event.stopPropagation();

      const element = event.target as HTMLInputElement;
      element.classList.add('border-dashed');
      element.classList.remove('border-solid');
      element.classList.remove('bg-primary-600');
      const inputFile = (event as DragEvent)?.dataTransfer?.files?.[0] || element?.files?.[0];
      if (inputFile) {
        // check file type
        const fileName = inputFile.name?.toLowerCase();
        if (fileName) {
          const supportedFormats = ['pdf', 'png', 'jpg', 'jpeg'];
          const ext = fileName.split('.').pop();
          if (ext && supportedFormats.indexOf(ext) === -1) {
            return;
          }
        }
        const file = BbitFileUtils.createAttachmentFromFile(inputFile);
        const prepareBody: IBbitPrepareFileUploadRequest = {
          fileName: file.fileName,
          contentType: file.contentType,
          contentLength: file.contentLength || 0,
        };
        const prepareResult = await fetch(`${this._website.getBlApiUrl()}/account/prepareFileUpload`, {
          method: 'POST',
          headers: {
            Authorization: `${this._website.getAccessToken()}`,
          },
          body: JSON.stringify(prepareBody),
        });
        if (!prepareResult.ok) {
          throw new Error("Couldn't prepare file upload");
        }
        const prepareResponse: IBbitPrepareFileUploadResponse = await prepareResult.json();

        const putResult = await fetch(prepareResponse.putUrl, {
          method: 'PUT',
          body: inputFile,
          headers: {
            'Content-Type': inputFile.type,
          },
        });
        if (!putResult.ok) {
          throw new Error("Couldn't upload file");
        }
        const linkBody: IBbitPlaceBookingFileLinkRequest = {
          type: this.fileType(),
          file: prepareResponse.file,
        };
        const linkResult = await fetch(`${this._website.getBlApiUrl()}/placeBooking/linkFile`, {
          method: 'POST',
          headers: {
            Authorization: `${this._website.getAccessToken()}`,
          },
          body: JSON.stringify(linkBody),
        });
        if (!linkResult.ok) {
          throw new Error("Couldn't link file to contact");
        }
        this.meta.reload();
        this.reload.emit();
      }
    } catch (err) {
      console.error(err);
    } finally {
      this.isUploading.set(false);
    }
  }

  dragOver(event: DragEvent) {
    const element = event.target as HTMLInputElement;
    element.classList.remove('border-dashed');
    element.classList.add('border-solid');
    element.classList.add('bg-primary-600');
    // element.style.border = '1px solid hsl(130, 100%, 26%)';
    event.preventDefault();
    event.stopPropagation();
  }
}
